/**
 * The Summary tab's half of the analyst seam — the second module that knows
 * QueVee's URL.
 *
 * Follows `compile-expr.ts`'s rules exactly, because they are the app's rules
 * for talking to a service: the base URL is read **once at module load**, the
 * trailing slash is stripped at the call site, the export is `undefined` when
 * the URL is unset, and **it never throws**. A rejected promise inside a view
 * renders as nothing at all; a returned failure renders as a sentence saying
 * what went wrong.
 *
 * Relative through Vite's proxy (`VITE_QUEVEE_URL=/`), not an absolute
 * loopback: `127.0.0.1:8002` from a phone on the LAN is *the phone*, and an
 * absolute origin is the only thing CORS would apply to. `vite.config.ts` says
 * all of this at length for `/dsl`; `/analyst` is proxied beside it.
 */

import { authHeaders } from "../lib/session";

const QUEVEE_URL = import.meta.env.VITE_QUEVEE_URL ?? "";

/** What the browser knows and the service cannot look up. */
export interface ChartContext {
  interval: string;
  barsShown: number;
  /** The aliases actually plotted, so the note can name the reader's own indicators. */
  indicators: string[];
  /** Last value per plotted series — `close` and every indicator alike. */
  latest: Record<string, number>;
  /**
   * The visible closes, thinned to ~32 points, oldest first.
   *
   * A last value is a position and this is a shape. Without it the note can say
   * price is above its 50-day and nothing more — no base, no breakout, no lower
   * high — and with no image it would have no trend information at all.
   */
  closePath?: number[];
  windowLow?: number;
  windowHigh?: number;
  /** Change across the visible window, as a fraction. */
  windowChange?: number;
  /** Bare base64 PNG, no `data:` prefix. Empty when the capture was not possible. */
  imagePng?: string;
}

/** One analyst's note, as the validator guarantees it. */
export interface StockSummary {
  verdict: "Constructive" | "Neutral" | "Cautious";
  thesis: string;
  fundamentals: string[];
  technicals: string[];
  bull: string[];
  bear: string[];
  watch: string;
  risk: string;
  /** 0 to 1. Never a percentage — the server refuses one and retries. */
  confidence: number;
  /** What the model was not given. Empty is its good answer. */
  missing: string[];
  /** Where the evidence first pointed, before the note argued against itself. */
  leaning?: "Constructive" | "Neutral" | "Cautious";
  /** The strongest case against the leaning — written before the verdict. */
  counterCase?: string;
}

/** Tokens and the billed cost of one part of a Summary, as every reader sees it. */
export interface SummaryUsage {
  inputTokens: number;
  outputTokens: number;
  calls: number;
  /** No longer sent — the model shows in ☰ Admin › AI only. Never rendered. */
  model?: string;
  /** Billed USD: list × the vendor's markup; null when unpriced. */
  costUsd?: number | null;
}

/** What the reader's own indicators add, read against the common verdict. */
export type PersonalPart =
  | { points: string[]; agrees: "agrees" | "mixed" | "disagrees"; usage?: SummaryUsage }
  | { error: string };

export type SummaryResult =
  | {
      ok: true;
      symbol: string;
      summary: StockSummary;
      /** What the note was written from, so a thin one can be told from a thorough one. */
      sources: { chartImage: boolean; missing: string[] };
      usage?: SummaryUsage | null;
      /** When this note was written (ISO, UTC). Shown at any age; a reader refreshes. */
      asOf?: string;
      /** True when this is the latest note, false for one picked from the history. */
      latest?: boolean;
      /** Every note for this symbol and plan level, newest first — the "As of" list. */
      history?: { writtenAt: string; verdict?: string | null }[];
      /** True when this note came from the cache and cost nothing this time. */
      cached?: boolean;
      /** May write a new note now: an admin always, a member once the latest is 15 min old. */
      canRefresh?: boolean;
      /** When a member may next refresh (ISO), while inside the cooldown. */
      refreshAt?: string | null;
      /** Show tokens and the billed cost — true for everyone who can open a Summary. */
      showCost?: boolean;
      /** Present only when the chart carries indicators outside the standard set. */
      personal?: PersonalPart | null;
    }
  | { ok: false; error: { message: string } };

/**
 * Ask QueVee for a note on one symbol.
 *
 * **Costs a model call**, so this is reached from a tab open and a Re-run
 * button, never from a render — the same argument `describeExpr` makes for a
 * far cheaper call. `lib/symbol-summary.ts` is what enforces it.
 */
export const fetchSummary:
  | ((
      symbol: string,
      chart: ChartContext,
      refresh?: boolean,
      asOf?: string,
    ) => Promise<SummaryResult>)
  | undefined = QUEVEE_URL
  ? async (symbol, chart, refresh = false, asOf?: string): Promise<SummaryResult> => {
      try {
        const response = await fetch(`${QUEVEE_URL.replace(/\/$/, "")}/analyst/summary`, {
          method: "POST",
          // quevee asks qf-us who the caller is with this token; without it
          // every summary is a 401 — reported as "Summary returned 401".
          headers: { "content-type": "application/json", ...(await authHeaders()) },
          body: JSON.stringify({
            symbol,
            chart,
            ...(refresh ? { refresh: true } : {}),
            // A past note from the "As of" list: free, no model call.
            ...(asOf ? { asOf } : {}),
          }),
        });
        if (!response.ok) {
          // A non-2xx means the request itself was wrong. The service answers
          // everything it can explain — no key, no data, a model that would not
          // produce the shape — with 200 and `ok: false`.
          return { ok: false, error: { message: `Summary returned ${response.status}.` } };
        }
        return (await response.json()) as SummaryResult;
      } catch (cause) {
        return {
          ok: false,
          error: {
            message:
              cause instanceof Error
                ? `Could not reach QueVee: ${cause.message}`
                : "Could not reach QueVee.",
          },
        };
      }
    }
  : undefined;

/** One provider the Summary can be written by. Whether its key is set, never the key. */
export interface SummaryProvider {
  id: string;
  label: string;
  model: string;
  hasKey: boolean;
  /** The env var the key goes in on quevee. */
  keyEnv: string;
}

/** One picker on ☰ Admin › AI: the choice, and every provider it may name. */
export interface ProviderChoice {
  provider: string;
  updatedAt: string | null;
  updatedBy: string | null;
  providers: SummaryProvider[];
}

/** ☰ Admin › AI: which provider writes the Summary, which Describe, and which runs the chat. */
export interface SummarySettings extends ProviderChoice {
  /** English → DSL's own choice. Absent from a quevee older than the picker. */
  describe?: ProviderChoice;
  /** The QueVee chat's own choice. Absent from a quevee older than the picker. */
  chat?: ProviderChoice;
}

export type SummarySettingsResult =
  | { ok: true; settings: SummarySettings }
  | { ok: false; error: string };

async function settingsCall(init?: RequestInit): Promise<SummarySettingsResult> {
  if (!QUEVEE_URL) return { ok: false, error: "QueVee is not configured (VITE_QUEVEE_URL)." };
  try {
    const response = await fetch(`${QUEVEE_URL.replace(/\/$/, "")}/analyst/settings`, {
      ...init,
      headers: { "content-type": "application/json", ...(await authHeaders()) },
    });
    if (!response.ok) {
      // The service's own sentence when it sent one: "DeepSeek needs DEEPSEEK_API_KEY…".
      const body = (await response.json().catch(() => null)) as { detail?: unknown } | null;
      const detail = typeof body?.detail === "string" ? body.detail : `status ${response.status}`;
      return { ok: false, error: `Could not ${init ? "save" : "load"} the AI settings: ${detail}.` };
    }
    return { ok: true, settings: (await response.json()) as SummarySettings };
  } catch (cause) {
    return {
      ok: false,
      error: cause instanceof Error ? `Could not reach QueVee: ${cause.message}` : "Could not reach QueVee.",
    };
  }
}

/** The current choice and every provider's model and key flag. Admin only; never throws. */
export function fetchSummarySettings(): Promise<SummarySettingsResult> {
  return settingsCall();
}

/** Switch the Summary's provider. Admin only; never throws. */
export function saveSummaryProvider(provider: string): Promise<SummarySettingsResult> {
  return settingsCall({ method: "PUT", body: JSON.stringify({ provider }) });
}

/** Switch Describe's provider; the Summary's is left alone. Admin only; never throws. */
export function saveDescribeProvider(provider: string): Promise<SummarySettingsResult> {
  return settingsCall({ method: "PUT", body: JSON.stringify({ describe: provider }) });
}

/** Switch the chat's provider; the other two are left alone. Admin only; never throws. */
export function saveChatProvider(provider: string): Promise<SummarySettingsResult> {
  return settingsCall({ method: "PUT", body: JSON.stringify({ chat: provider }) });
}
